import { Box, IconButton } from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { Link } from "react-router";
import { useTranslation } from "@localization/useTranslation";
import FrameworkControlStatusChip from "../../../framework/frameworkOverview/frameworkControlStatusChip/FrameworkControlStatusChip";
import { useUrlProvider } from "../../../useUrlProvider";
import { PolicyControl } from "../PolicySections.types";

export interface PolicyControlChipProps {
    control: PolicyControl;
    onRemove?: () => void;
    // A white chip on a white section needs the primary stroke to read as a distinct item. On a tinted surface
    // that stroke competes with the background, so those hosts opt into the neutral divider instead.
    subduedStroke?: boolean;
}

function PolicyControlChip({ control, onRemove, subduedStroke }: PolicyControlChipProps) {
    const { translateFrameworkControlStatusEnum, translateString } = useTranslation();
    const { getFrameworkControlOverviewUrl } = useUrlProvider();

    return (
        <Box
            sx={(theme) => ({
                display: "inline-flex",
                alignItems: "center",
                gap: 0.75,
                paddingX: 1,
                paddingY: 0.5,
                borderRadius: 4,
                fontSize: 13,
                backgroundColor: theme.palette.background.paper,
                border: `1px solid ${subduedStroke ? theme.palette.divider : theme.palette.primary.main}`,
                transition: "border-color 0.15s ease",
                "&:has(a:hover)": {
                    borderColor: subduedStroke ? theme.palette.primary.main : theme.palette.primary.dark,
                },
            })}
        >
            <Box
                component={Link}
                to={getFrameworkControlOverviewUrl(control.id)}
                onClick={(event) => event.stopPropagation()}
                sx={{ display: "inline-flex", alignItems: "center", gap: 0.75, textDecoration: "none", "&:hover": { textDecoration: "none" } }}
            >
                {control.customId && (
                    <Box component="span" sx={(theme) => ({ fontWeight: 700, color: theme.palette.primary.dark })}>
                        {control.customId}
                    </Box>
                )}
                <Box component="span" sx={(theme) => ({ color: theme.palette.primary.dark })}>
                    {control.name}
                </Box>
            </Box>
            <FrameworkControlStatusChip isExcluded={false} status={control.status} value={translateFrameworkControlStatusEnum(control.status.toString())} />
            {onRemove && (
                <IconButton
                    size="small"
                    aria-label={translateString("remove")}
                    onClick={(event) => {
                        event.stopPropagation();
                        onRemove();
                    }}
                    sx={{ padding: 0.25 }}
                >
                    <CloseIcon sx={{ fontSize: 14 }} />
                </IconButton>
            )}
        </Box>
    );
}

export default PolicyControlChip;
